// Header del trámite: título, estado, barra de progreso, nº expediente
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import type { ExpedienteWorkspaceSummary } from '@/types/clienteFicha';

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  new: { label: 'Nuevo', variant: 'secondary' },
  in_progress: { label: 'En proceso', variant: 'default' },
  pending_info: { label: 'Pendiente de información', variant: 'destructive' },
  completed: { label: 'Completado', variant: 'outline' },
  archived: { label: 'Archivado', variant: 'secondary' },
};

interface Props {
  workspace: ExpedienteWorkspaceSummary;
}

export function ClienteTramiteHeader({ workspace }: Props) {
  const statusInfo = STATUS_MAP[workspace.status] || { label: workspace.status, variant: 'secondary' as const };
  const pct = workspace.completitud_pct ?? 0;

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        {/* Título y estado */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {workspace.titulo || workspace.formulario_nombre || 'Mi Trámite'}
            </h2>
            {workspace.numero_expediente_oficial && (
              <p className="text-sm text-gray-500 mt-0.5">
                Nº Expediente: {workspace.numero_expediente_oficial}
              </p>
            )}
          </div>
          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
        </div>

        {/* Barra de progreso */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Progreso del trámite</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{Math.round(pct)}%</span>
          </div>
          <Progress value={pct} className="h-3" />
        </div>

        {/* Próximos pasos */}
        {workspace.next_steps && Object.keys(workspace.next_steps).length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">Próximos pasos</p>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-0.5">
              {Object.entries(workspace.next_steps).map(([key, value]) => (
                <li key={key}>• {String(value)}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Alerta de riesgo */}
        {workspace.riesgo_global && workspace.riesgo_global !== 'bajo' && (
          <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Nivel de riesgo: <span className="capitalize">{workspace.riesgo_global}</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
